import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Dialog, Input, Label } from '@attendance/ui';
import { Trans } from 'react-i18next';

/**
 * Replaces `window.confirm()` with the accessible Dialog primitive from
 * `@attendance/ui`. Six pages used `confirm()` for delete confirmations
 * (LeaveTypes, Holidays, Departments, Designations, Shifts, Announcements);
 * each one was a tiny brand-breaking ux dead-zone — native `confirm()` can't
 * be styled, blocks the event loop, drops the dark theme, and is the only
 * focus trap on the page that the existing `Dialog` primitive doesn't beat.
 *
 * Usage:
 *
 *   const { confirm, ConfirmPortal } = useConfirm();
 *
 *   <button onClick={async () => {
 *     if (await confirm({ title: 'Delete shift?', body: 'This cannot be undone.', destructive: true })) {
 *       remove.mutate(id);
 *     }
 *   }}>Delete</button>
 *
 *   // Anywhere in the tree (typically next to the page's other dialogs):
 *   <ConfirmPortal />
 *
 * `confirm()` returns a Promise<boolean> — true on confirm, false on
 * cancel/escape/overlay-click. Mirrors the native `confirm()` contract
 * so the migration is one-line-per-site.
 */
export interface ConfirmOptions {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /**
   * The close ×'s accessible name. Taken as an option like the other two,
   * because this hook has no `t` of its own — and the × is the only control in
   * the dialog with no visible word, so it is the one that most needs it.
   */
  closeLabel?: string;
  /** Renders the confirm button in the destructive (red) variant. */
  destructive?: boolean;
  /**
   * Require the operator to type this exact phrase before the confirm button
   * enables. Reserve it for actions that destroy evidence rather than rows —
   * a dialog you can dismiss with one reflexive click is not a speed bump.
   *
   * Undefined (the default) keeps every existing caller's behaviour: a plain
   * confirm with an auto-focused button. Only opt in where the action is
   * genuinely irreversible, or the phrase becomes muscle memory and stops
   * being a control at all.
   */
  requireTyped?: string;
}

interface PendingConfirm extends ConfirmOptions {
  resolve: (ok: boolean) => void;
}

/**
 * The `confirm()` half on its own, so a hook that BUILDS row verbs can take one
 * as an argument instead of calling `useConfirm` again. Two calls would mount
 * two portals — one of them inside a table cell's closure — for one dialog.
 */
export type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

export function useConfirm(): {
  confirm: ConfirmFn;
  ConfirmPortal: () => JSX.Element | null;
} {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirm = useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setPending({ ...opts, resolve });
      }),
    []
  );

  const close = useCallback(
    (ok: boolean): void => {
      pending?.resolve(ok);
      setPending(null);
    },
    [pending]
  );

  // Bind the portal to the pending state so the component is stable across
  // renders — passing it down by reference doesn't recreate.
  const ConfirmPortal = useMemo(
    () =>
      function ConfirmPortalImpl(): JSX.Element | null {
        // Typed state lives HERE, not in the hook body. The component identity
        // is memoised on `pending`, so it stays stable while somebody types
        // (no remount, no detached nodes, no lost focus) and is recreated when
        // a confirm opens or closes — which resets the phrase for free. Lifting
        // this to the hook would put `typed` in the memo deps and remount the
        // dialog on every keystroke.
        const [typed, setTyped] = useState('');
        if (!pending) return null;
        const phrase = pending.requireTyped;
        // Exact match, deliberately: no trim, no case-folding. The phrase is a
        // deliberate act, and "delete photos" typed absent-mindedly should not
        // unlock a permanent deletion.
        const unlocked = phrase === undefined || typed === phrase;
        return (
          <Dialog
            closeLabel={pending.closeLabel ?? 'Close'}
            open
            onClose={() => close(false)}
            title={pending.title}
            description={typeof pending.body === 'string' ? pending.body : undefined}
            /* `sm` — a confirm is a sentence and two buttons, not a form. This
               was the one dialog in the app still getting the `md` (480px)
               default meant for a single-column form, which is exactly the
               box `Dialog`'s own `DIALOG_MAX` comment and `ConfirmDialog`
               (its sibling wrapper, unused by this hook) both call out as the
               wrong fit for a confirmation. Every org-screen delete prompt —
               Branches, Departments, Designations, Labour suppliers — routes
               through this one hook, so the fix lands on all four at once. */
            size="sm"
            /* The pair, weighted, from the primitive — see the action-row
               rule on `Dialog`. A confirm keeps its "no" as a labelled control
               because the action it guards cannot be undone; it just does not
               earn a border, which would make refusing and confirming read as
               two equally-weighted choices. */
            dismiss={{ label: pending.cancelLabel ?? 'Cancel', onClick: () => close(false) }}
            confirm={{
              label: pending.confirmLabel ?? 'Confirm',
              onClick: () => close(true),
              tone: pending.destructive ? 'danger' : 'default',
              disabled: !unlocked,
              // Don't steal focus onto a button the operator cannot press yet —
              // when a phrase is required, the input is the first thing they
              // need.
              autoFocus: phrase === undefined,
            }}
          >
            {typeof pending.body !== 'string' && pending.body ? (
              <div>{pending.body}</div>
            ) : null}
            {phrase !== undefined && (
              <div className="mt-4 space-y-control">
                <Label htmlFor="confirm-phrase">
                  {/* <Trans>, not two interpolations around a span: the phrase
                      sits INSIDE the sentence and Arabic does not place it where
                      English does. Splitting this into a prefix and a suffix
                      would hard-code English word order into the markup. */}
                  <Trans
                    i18nKey="confirm.typeToConfirm"
                    defaults="Type <0>{{phrase}}</0> to confirm"
                    values={{ phrase }}
                    components={[<span className="font-mono font-semibold" />]}
                  />
                </Label>
                <Input
                  id="confirm-phrase"
                  value={typed}
                  autoFocus
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(e) => setTyped(e.target.value)}
                />
              </div>
            )}
          </Dialog>
        );
      },
    [pending, close]
  );

  return { confirm, ConfirmPortal };
}
